import React, { useEffect, useMemo, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { FileUp, History, LogOut, Play, Plus, RefreshCw, ShieldCheck } from 'lucide-react';
import { clearTokens, decodeClaims, exchangeCode, getStoredTokens, signIn, signOut, AuthTokens } from './auth';
import {
  abortJob,
  approveBidMemo,
  approveJobCostOverrun,
  AuditEvent,
  Artifact,
  createDeal,
  Deal,
  getMandateConfig,
  latestArtifact,
  listArtifacts,
  listDeals,
  listAuditEvents,
  listJobs,
  Job,
  MandateConfig,
  requestCimUpload,
  rerunPipeline,
  upsertMandateConfig,
} from './graphql';
import { artifactKey, artifactPayloadFrom, EvidenceRow, extractEvidenceRows, summarizeEvidence } from './artifacts';
import { hasActiveJobs, shouldPollPipeline, uploadPresignedPost } from './pipeline';
import './styles.css';

const artifactTypes = ['shredder_run', 'normalization_run', 'research_pack', 'lbo_run', 'risks_run', 'bid_memo'];

function Callback() {
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get('code');
    if (!code) {
      setError('Missing authorization code');
      return;
    }
    exchangeCode(code)
      .then(() => window.location.assign('/'))
      .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)));
  }, []);
  return <main className="center">{error ? <p className="error">{error}</p> : <p>Completing sign in...</p>}</main>;
}

function App() {
  const [tokens, setTokens] = useState<AuthTokens | null>(() => getStoredTokens());
  const [deals, setDeals] = useState<Deal[]>([]);
  const [selectedDealId, setSelectedDealId] = useState('');
  const [jobs, setJobs] = useState<Job[]>([]);
  const [auditEvents, setAuditEvents] = useState<AuditEvent[]>([]);
  const [artifact, setArtifact] = useState<Artifact | null>(null);
  const [artifactHistory, setArtifactHistory] = useState<Artifact[]>([]);
  const [mandate, setMandate] = useState<MandateConfig | null>(null);
  const [mandateDraft, setMandateDraft] = useState({
    sectors: 'Business services, Industrial services',
    geography: 'United States',
    minEbitda: '5',
    maxEbitda: '25',
    minEnterpriseValue: '25',
    maxEnterpriseValue: '150',
    minIrr: '0.22',
    minMoic: '2.5',
    minDscr: '1.35',
    minFccr: '1.15',
    thesisBreakers: 'Customer concentration without mitigation, Declining EBITDA quality',
    houseVoice: 'Direct, evidence-first, partner-ready.',
  });
  const [newDealName, setNewDealName] = useState('Project Apex');
  const [selectedArtifact, setSelectedArtifact] = useState('bid_memo');
  const [selectedCimFile, setSelectedCimFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState('');
  const [pipelinePolling, setPipelinePolling] = useState(false);
  const [pipelineWatchUntil, setPipelineWatchUntil] = useState(0);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const claims = useMemo(() => (tokens ? decodeClaims(tokens.id_token) : null), [tokens]);
  const selectedDeal = deals.find((deal) => deal.dealId === selectedDealId);
  const artifactPayload = useMemo(() => {
    if (!artifact) return null;
    try {
      return JSON.parse(artifact.payload) as Record<string, unknown>;
    } catch {
      return null;
    }
  }, [artifact]);
  const evidenceSummary = useMemo(() => summarizeEvidence(artifactPayload), [artifactPayload]);
  const evidenceRows = useMemo(() => extractEvidenceRows(artifactPayload), [artifactPayload]);
  const canApproveMemo = ['vp', 'admin'].includes(String(claims?.['custom:deal_pod_role'] ?? ''));
  const canEditMandate = canApproveMemo;
  const pipelineIsActive = pipelinePolling || hasActiveJobs(jobs);
  const completeJobCount = jobs.filter((job) => job.status === 'complete').length;
  const pipelineProgress = jobs.length > 0 ? Math.round((completeJobCount / jobs.length) * 100) : 0;

  async function refresh(nextDealId = selectedDealId, watchUntil = pipelineWatchUntil) {
    if (!tokens) return;
    setError(null);
    const [dealRows, mandateConfig] = await Promise.all([listDeals(tokens.id_token), getMandateConfig(tokens.id_token)]);
    setMandate(mandateConfig);
    if (mandateConfig) {
      setMandateDraft({
        sectors: mandateConfig.sectors.join(', '),
        geography: mandateConfig.geography.join(', '),
        minEbitda: mandateConfig.minEbitda?.toString() ?? '',
        maxEbitda: mandateConfig.maxEbitda?.toString() ?? '',
        minEnterpriseValue: mandateConfig.minEnterpriseValue?.toString() ?? '',
        maxEnterpriseValue: mandateConfig.maxEnterpriseValue?.toString() ?? '',
        minIrr: mandateConfig.minIrr?.toString() ?? '',
        minMoic: mandateConfig.minMoic?.toString() ?? '',
        minDscr: mandateConfig.minDscr?.toString() ?? '',
        minFccr: mandateConfig.minFccr?.toString() ?? '',
        thesisBreakers: mandateConfig.thesisBreakers.join(', '),
        houseVoice: mandateConfig.houseVoice ?? '',
      });
    }
    setDeals(dealRows);
    const activeDealId = nextDealId || dealRows[0]?.dealId || '';
    setSelectedDealId(activeDealId);
    if (activeDealId) {
      const [jobRows, latest, auditRows, historyRows] = await Promise.all([
        listJobs(tokens.id_token, activeDealId),
        latestArtifact(tokens.id_token, activeDealId, selectedArtifact),
        listAuditEvents(tokens.id_token, activeDealId),
        listArtifacts(tokens.id_token, activeDealId, selectedArtifact),
      ]);
      setJobs(jobRows);
      setArtifact(latest);
      setAuditEvents(auditRows.slice(0, 8));
      setArtifactHistory(historyRows);
      setPipelinePolling(shouldPollPipeline(jobRows, watchUntil));
      setLastRefreshedAt(new Date().toISOString());
    } else {
      setJobs([]);
      setArtifact(null);
      setArtifactHistory([]);
      setAuditEvents([]);
      setPipelinePolling(false);
      setLastRefreshedAt(new Date().toISOString());
    }
  }

  useEffect(() => {
    refresh().catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)));
  }, [tokens, selectedArtifact]);

  useEffect(() => {
    if (!tokens || !selectedDealId || !pipelinePolling) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const [jobRows, latest, auditRows, historyRows] = await Promise.all([
          listJobs(tokens.id_token, selectedDealId),
          latestArtifact(tokens.id_token, selectedDealId, selectedArtifact),
          listAuditEvents(tokens.id_token, selectedDealId),
          listArtifacts(tokens.id_token, selectedDealId, selectedArtifact),
        ]);
        if (cancelled) return;
        setJobs(jobRows);
        setArtifact(latest);
        setAuditEvents(auditRows.slice(0, 8));
        setArtifactHistory(historyRows);
        setPipelinePolling(shouldPollPipeline(jobRows, pipelineWatchUntil));
        setLastRefreshedAt(new Date().toISOString());
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    };
    const interval = window.setInterval(() => {
      void poll();
    }, 4000);
    void poll();
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [tokens, selectedDealId, selectedArtifact, pipelinePolling, pipelineWatchUntil]);

  if (window.location.pathname === '/callback') return <Callback />;

  if (!tokens) {
    return (
      <main className="login">
        <section>
          <ShieldCheck size={36} />
          <h1>Diligence OS</h1>
          <p>CIM to comp-grounded bid memo, backed by Cognito, AppSync, DynamoDB, S3, and Step Functions.</p>
          <button onClick={() => signIn()}>Sign in</button>
        </section>
      </main>
    );
  }

  return (
    <main className="app">
      <aside>
        <div className="brand">Diligence OS</div>
        <div className="identity">
          <strong>{String(claims?.email ?? 'Authenticated user')}</strong>
          <span>Tenant {String(claims?.['custom:tenant_id'] ?? 'unassigned')}</span>
        </div>
        <button className="ghost" onClick={() => { clearTokens(); setTokens(null); }}>
          Local sign out
        </button>
        <button className="ghost" onClick={() => signOut()}>
          <LogOut size={16} /> Cognito sign out
        </button>
      </aside>

      <section className="workspace">
        <header>
          <div>
            <h1>Deal Workspace</h1>
            <p>Phase A plumbing harness for the CIM-to-bid pipeline.</p>
          </div>
          <button className="ghost" onClick={() => refresh()}>
            <RefreshCw size={16} /> Refresh
          </button>
        </header>

        {error && <div className="banner">{error}</div>}

        <section className="toolbar">
          <label>
            Deal
            <select value={selectedDealId} onChange={(event) => refresh(event.target.value)}>
              <option value="">Select deal</option>
              {deals.map((deal) => (
                <option key={deal.dealId} value={deal.dealId}>{deal.name}</option>
              ))}
            </select>
          </label>
          <label>
            New deal
            <input value={newDealName} onChange={(event) => setNewDealName(event.target.value)} />
          </label>
          <button
            onClick={async () => {
              setBusy(true);
              try {
                const deal = await createDeal(tokens.id_token, newDealName);
                await refresh(deal.dealId);
              } catch (err) {
                setError(err instanceof Error ? err.message : String(err));
              } finally {
                setBusy(false);
              }
            }}
          >
            <Plus size={16} /> Create
          </button>
        </section>

        <section className="panel">
          <div className="panelHeader">
            <div>
              <h2>Mandate Overlay</h2>
              <p className="muted">Version {mandate?.version ?? 0}. Snapshotted when a deal is opened.</p>
            </div>
            <button
              disabled={busy || !canEditMandate}
              title={canEditMandate ? 'Save mandate' : 'Requires VP or admin role'}
              onClick={async () => {
                setBusy(true);
                try {
                  const saved = await upsertMandateConfig(tokens.id_token, {
                    sectors: splitList(mandateDraft.sectors),
                    geography: splitList(mandateDraft.geography),
                    minEbitda: numberOrUndefined(mandateDraft.minEbitda),
                    maxEbitda: numberOrUndefined(mandateDraft.maxEbitda),
                    minEnterpriseValue: numberOrUndefined(mandateDraft.minEnterpriseValue),
                    maxEnterpriseValue: numberOrUndefined(mandateDraft.maxEnterpriseValue),
                    minIrr: numberOrUndefined(mandateDraft.minIrr),
                    minMoic: numberOrUndefined(mandateDraft.minMoic),
                    minDscr: numberOrUndefined(mandateDraft.minDscr),
                    minFccr: numberOrUndefined(mandateDraft.minFccr),
                    thesisBreakers: splitList(mandateDraft.thesisBreakers),
                    houseVoice: mandateDraft.houseVoice,
                  });
                  setMandate(saved);
                } catch (err) {
                  setError(err instanceof Error ? err.message : String(err));
                } finally {
                  setBusy(false);
                }
              }}
            >
              Save mandate
            </button>
          </div>
          <div className="mandateGrid">
            <label>Sectors<input value={mandateDraft.sectors} onChange={(event) => setMandateDraft({ ...mandateDraft, sectors: event.target.value })} /></label>
            <label>Geography<input value={mandateDraft.geography} onChange={(event) => setMandateDraft({ ...mandateDraft, geography: event.target.value })} /></label>
            <label>Min EBITDA<input value={mandateDraft.minEbitda} onChange={(event) => setMandateDraft({ ...mandateDraft, minEbitda: event.target.value })} /></label>
            <label>Max EBITDA<input value={mandateDraft.maxEbitda} onChange={(event) => setMandateDraft({ ...mandateDraft, maxEbitda: event.target.value })} /></label>
            <label>Min EV<input value={mandateDraft.minEnterpriseValue} onChange={(event) => setMandateDraft({ ...mandateDraft, minEnterpriseValue: event.target.value })} /></label>
            <label>Max EV<input value={mandateDraft.maxEnterpriseValue} onChange={(event) => setMandateDraft({ ...mandateDraft, maxEnterpriseValue: event.target.value })} /></label>
            <label>Min IRR<input value={mandateDraft.minIrr} onChange={(event) => setMandateDraft({ ...mandateDraft, minIrr: event.target.value })} /></label>
            <label>Min MOIC<input value={mandateDraft.minMoic} onChange={(event) => setMandateDraft({ ...mandateDraft, minMoic: event.target.value })} /></label>
            <label>Min DSCR<input value={mandateDraft.minDscr} onChange={(event) => setMandateDraft({ ...mandateDraft, minDscr: event.target.value })} /></label>
            <label>Min FCCR<input value={mandateDraft.minFccr} onChange={(event) => setMandateDraft({ ...mandateDraft, minFccr: event.target.value })} /></label>
            <label className="wide">Thesis breakers<input value={mandateDraft.thesisBreakers} onChange={(event) => setMandateDraft({ ...mandateDraft, thesisBreakers: event.target.value })} /></label>
            <label className="wide">House voice<input value={mandateDraft.houseVoice} onChange={(event) => setMandateDraft({ ...mandateDraft, houseVoice: event.target.value })} /></label>
          </div>
        </section>

        <section className="grid">
          <div className="panel">
            <h2>{selectedDeal?.name ?? 'No deal selected'}</h2>
            <p className="muted">{selectedDealId || 'Create or select a deal to run the pipeline.'}</p>
            <label className="filePicker">
              CIM file
              <input
                accept=".pdf,.txt,.md,.doc,.docx,application/pdf,text/plain,text/markdown,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                disabled={!selectedDealId || busy}
                type="file"
                onChange={(event) => {
                  const file = event.target.files?.[0] ?? null;
                  setSelectedCimFile(file);
                  setUploadStatus(file ? `${file.name} selected (${formatBytes(file.size)})` : '');
                }}
              />
            </label>
            <button
              disabled={!selectedDealId || !selectedCimFile || busy}
              onClick={async () => {
                if (!selectedCimFile) return;
                setBusy(true);
                setUploadStatus(`Requesting upload URL for ${selectedCimFile.name}...`);
                try {
                  const contentType = selectedCimFile.type || 'application/octet-stream';
                  const upload = await requestCimUpload(tokens.id_token, selectedDealId, selectedCimFile.name, contentType, selectedCimFile.size);
                  setUploadStatus(`Uploading ${selectedCimFile.name} to S3...`);
                  await uploadPresignedPost(upload, selectedCimFile);
                  const watchUntil = Date.now() + 2 * 60 * 1000;
                  setUploadStatus(`Uploaded ${selectedCimFile.name}. S3 event will start the pipeline.`);
                  setPipelineWatchUntil(watchUntil);
                  setPipelinePolling(true);
                  await refresh(selectedDealId, watchUntil);
                } catch (err) {
                  setError(err instanceof Error ? err.message : String(err));
                  setUploadStatus('');
                } finally {
                  setBusy(false);
                }
              }}
            >
              <Play size={16} /> Upload CIM + start pipeline
            </button>
            <button
              className="ghost"
              disabled={!selectedDealId || busy}
              onClick={async () => {
                const reason = window.prompt('Rerun reason', 'Manual rerun from latest uploaded CIM');
                if (!reason) return;
                setBusy(true);
                try {
                  const execution = await rerunPipeline(tokens.id_token, selectedDealId, reason);
                  const watchUntil = Date.now() + 2 * 60 * 1000;
                  setUploadStatus(`Rerun started from ${execution.cimDocumentId}.`);
                  setPipelineWatchUntil(watchUntil);
                  setPipelinePolling(true);
                  await refresh(selectedDealId, watchUntil);
                } catch (err) {
                  setError(err instanceof Error ? err.message : String(err));
                } finally {
                  setBusy(false);
                }
              }}
            >
              <RefreshCw size={16} /> Rerun latest CIM
            </button>
            <p className="hint"><FileUp size={14} /> Upload writes the actual selected file to S3. The S3 ObjectCreated event starts the pipeline.</p>
            {uploadStatus && <p className="uploadStatus">{uploadStatus}</p>}
          </div>

          <div className="panel">
            <div className="panelHeader">
              <div>
                <h2>Pipeline Jobs</h2>
                <p className="muted">{pipelineIsActive ? 'Pipeline running.' : 'Latest job state.'}</p>
              </div>
              {lastRefreshedAt && <time className="lastRefresh">Updated {new Date(lastRefreshedAt).toLocaleTimeString()}</time>}
            </div>
            {jobs.length > 0 && (
              <div className="pipelineProgress" aria-label={`Pipeline ${pipelineProgress}% complete`}>
                <div>
                  <span style={{ width: `${pipelineProgress}%` }} />
                </div>
                <strong>{completeJobCount}/{jobs.length} complete</strong>
              </div>
            )}
            <div className="jobs">
              {jobs.length === 0 && <p className="muted">No jobs yet.</p>}
              {jobs.map((job) => (
                <div key={`${job.jobType}-${job.jobId}`} className="job">
                  <div>
                    <span>{job.jobType}</span>
                    {(job.costCeilingUsd != null || job.costUsedUsd != null) && (
                      <small>
                        ${Number(job.costUsedUsd ?? 0).toFixed(2)} / ${Number(job.costCeilingUsd ?? 0).toFixed(2)}
                      </small>
                    )}
                  </div>
                  <div className="jobState">
                    <strong className={job.status}>{job.status}</strong>
                    {job.status === 'awaiting_cost_approval' && (
                      <div className="jobActions">
                        <button
                          disabled={busy || !canApproveMemo}
                          title={canApproveMemo ? 'Approve cost ceiling and rerun pipeline' : 'Requires VP or admin role'}
                          onClick={async () => {
                            const current = Number(job.costCeilingUsd ?? 0);
                            const ceiling = window.prompt('New cost ceiling (USD)', String(Math.max(current * 2, current + 1)));
                            if (!ceiling) return;
                            const parsedCeiling = Number(ceiling);
                            if (!Number.isFinite(parsedCeiling) || parsedCeiling <= current) {
                              setError('New cost ceiling must be greater than the current ceiling.');
                              return;
                            }
                            const reason = window.prompt('Approval reason', 'LLM overrun approved for this run');
                            if (!reason) return;
                            setBusy(true);
                            try {
                              await approveJobCostOverrun(tokens.id_token, selectedDealId, job.jobId, parsedCeiling, reason);
                              const watchUntil = Date.now() + 2 * 60 * 1000;
                              setUploadStatus('Cost ceiling approved. Pipeline rerun started from the latest uploaded CIM.');
                              setPipelineWatchUntil(watchUntil);
                              setPipelinePolling(true);
                              await refresh(selectedDealId, watchUntil);
                            } catch (err) {
                              setError(err instanceof Error ? err.message : String(err));
                            } finally {
                              setBusy(false);
                            }
                          }}
                        >
                          Approve + rerun
                        </button>
                        <button
                          disabled={busy || !canApproveMemo}
                          title={canApproveMemo ? 'Abort job' : 'Requires VP or admin role'}
                          onClick={async () => {
                            const reason = window.prompt('Abort reason', 'LLM cost overrun not approved');
                            if (!reason) return;
                            setBusy(true);
                            try {
                              await abortJob(tokens.id_token, selectedDealId, job.jobId, reason);
                              await refresh(selectedDealId);
                            } catch (err) {
                              setError(err instanceof Error ? err.message : String(err));
                            } finally {
                              setBusy(false);
                            }
                          }}
                        >
                          Abort
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="panel">
          <div className="panelHeader">
            <div>
              <h2>Audit Trail</h2>
              <p className="muted">Newest immutable events for the selected deal.</p>
            </div>
            <History size={18} />
          </div>
          <div className="auditList">
            {auditEvents.length === 0 && <p className="muted">No audit events yet.</p>}
            {auditEvents.map((event) => (
              <div key={event.auditEventId} className="auditEvent">
                <div>
                  <strong>{event.action.replaceAll('_', ' ')}</strong>
                  <span>{event.targetEntity} · {event.source ?? 'system'}</span>
                </div>
                <time>{new Date(event.occurredAt).toLocaleString()}</time>
              </div>
            ))}
          </div>
        </section>

        <section className="panel">
          <div className="panelHeader">
            <div>
              <h2>Latest Artifact</h2>
              {artifactPayload && (
                <p className="muted">
                  {evidenceSummary.citationCount} citation(s) · {evidenceSummary.sectionCount} section(s) · {evidenceSummary.claimCount} claim(s)
                </p>
              )}
              {artifactPayload?.approvalStatus === 'approved' && (
                <p className="muted">Approved by {String(artifactPayload.approvedByRole ?? 'unknown')} at {new Date(String(artifactPayload.approvedAt)).toLocaleString()}</p>
              )}
            </div>
            <div className="artifactActions">
              {selectedArtifact === 'bid_memo' && artifactPayload?.bidMemoVersionId != null && artifactPayload.approvalStatus !== 'approved' && (
                <button
                  disabled={busy || !selectedDealId || !canApproveMemo}
                  title={canApproveMemo ? 'Approve bid memo' : 'Requires VP or admin role'}
                  onClick={async () => {
                    setBusy(true);
                    try {
                      const approved = await approveBidMemo(tokens.id_token, selectedDealId, String(artifactPayload.bidMemoVersionId));
                      setArtifact(approved);
                      await refresh(selectedDealId);
                    } catch (err) {
                      setError(err instanceof Error ? err.message : String(err));
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  Approve memo
                </button>
              )}
              <select value={selectedArtifact} onChange={(event) => setSelectedArtifact(event.target.value)}>
                {artifactTypes.map((type) => <option key={type} value={type}>{type}</option>)}
              </select>
            </div>
          </div>
          <div className="artifactGrid">
            <div>
              <pre>{artifactPayload ? JSON.stringify(artifactPayload, null, 2) : 'No artifact found.'}</pre>
            </div>
            <div className="evidencePanel" aria-label="Evidence summary">
              <h3>Evidence</h3>
              <dl>
                <div><dt>Citations</dt><dd>{evidenceSummary.citationCount}</dd></div>
                <div><dt>Sources</dt><dd>{evidenceSummary.sourceCount}</dd></div>
                <div><dt>Sections</dt><dd>{evidenceSummary.sectionCount}</dd></div>
                <div><dt>Claims</dt><dd>{evidenceSummary.claimCount}</dd></div>
              </dl>
              {artifactPayload?.mandateFitOverlay != null && <p className="evidenceNote">Mandate fit is present as a directional overlay.</p>}
              {artifactPayload?.textractJobId != null && <p className="evidenceNote">Textract job {String(artifactPayload.textractJobId)}</p>}
              <EvidenceList title="Claims" rows={evidenceRows.claims} emptyLabel="No claim rows on this artifact." />
              <EvidenceList title="Citations" rows={evidenceRows.citations} emptyLabel="No citation refs on this artifact." />
              <EvidenceList title="Sources" rows={evidenceRows.sources} emptyLabel="No source manifest on this artifact." />
              <h3>History</h3>
              <div className="artifactHistory">
                {artifactHistory.length === 0 && <p className="muted">No history yet.</p>}
                {[...artifactHistory].sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt)).slice(0, 8).map((row) => {
                  const rowPayload = artifactPayloadFrom(row);
                  const rowEvidence = summarizeEvidence(rowPayload);
                  const isSelected = artifactKey(row) === artifactKey(artifact);
                  const hasUnsupportedClaims = rowEvidence.claimCount > 0 && rowEvidence.citationCount === 0;
                  return (
                    <button
                      key={`${row.entityType}-${row.createdAt}-${String(rowPayload?.bidMemoVersionId ?? rowPayload?.shredderRunId ?? rowPayload?.status ?? '')}`}
                      className={isSelected ? 'historyItem selected' : 'historyItem'}
                      onClick={() => setArtifact(row)}
                    >
                      <span>{new Date(row.createdAt).toLocaleString()}</span>
                      <strong>{String(rowPayload?.approvalStatus ?? row.status)}</strong>
                      <small>{rowEvidence.claimCount} claims · {rowEvidence.citationCount} citations · {rowEvidence.sourceCount} sources</small>
                      {rowPayload?.version != null && <em>v{String(rowPayload.version)}</em>}
                      {hasUnsupportedClaims && <em className="warning">No citations</em>}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      </section>
    </main>
  );
}

function EvidenceList({ title, rows, emptyLabel }: { title: string; rows: EvidenceRow[]; emptyLabel: string }) {
  return (
    <section className="evidenceList" aria-label={`${title} evidence`}>
      <h4>{title}</h4>
      {rows.length === 0 && <p className="muted">{emptyLabel}</p>}
      {rows.slice(0, 6).map((row) => (
        <article key={row.id} className={`evidenceRow ${row.status ?? 'verified'}`}>
          <strong>{row.title}</strong>
          {row.detail && <span>{row.detail}</span>}
          {row.meta && <em>{row.meta}</em>}
        </article>
      ))}
      {rows.length > 6 && <p className="muted">Showing 6 of {rows.length}.</p>}
    </section>
  );
}

function splitList(value: string): string[] {
  return value.split(',').map((item) => item.trim()).filter(Boolean);
}

function numberOrUndefined(value: string): number | undefined {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

createRoot(document.getElementById('root')!).render(<App />);
